/**
 * User Title and Gender Helper Utilities
 * Handles title prefixes, gender mapping, and full name formatting
 */

// Gender constants
export const Gender = {
  MALE: 'Male',
  FEMALE: 'Female',
  OTHER: 'Other'
}

// Thai title constants
export const TitleTh = {
  MR: 'นาย',
  MRS: 'นาง',
  MISS: 'นางสาว',
  DR_MALE: 'ทพ.',
  DR_FEMALE: 'ทพญ.'
}

// English title constants
export const TitleEn = {
  MR: 'Mr.',
  MRS: 'Mrs.',
  MISS: 'Ms.',
  DR: 'Dr.'
}

/**
 * Get list of Thai title options for dropdown
 * @returns {Array} Array of {value, text} objects
 */
export function getThaiTitleOptions() {
  return [
    { value: '', text: 'ไม่ระบุ' },
    { value: TitleTh.MR, text: TitleTh.MR },
    { value: TitleTh.MRS, text: TitleTh.MRS },
    { value: TitleTh.MISS, text: TitleTh.MISS },
    { value: TitleTh.DR_MALE, text: TitleTh.DR_MALE },
    { value: TitleTh.DR_FEMALE, text: TitleTh.DR_FEMALE }
  ]
}

/**
 * Get list of English title options for dropdown
 * @returns {Array} Array of {value, text} objects
 */
export function getEnglishTitleOptions() {
  return [
    { value: '', text: 'Not specified' },
    { value: TitleEn.MR, text: TitleEn.MR },
    { value: TitleEn.MRS, text: TitleEn.MRS },
    { value: TitleEn.MISS, text: TitleEn.MISS },
    { value: TitleEn.DR, text: TitleEn.DR }
  ]
}

/**
 * Get list of gender options for dropdown
 * @returns {Array} Array of {value, text} objects
 */
export function getGenderOptions() {
  return [
    { value: null, text: 'ไม่ระบุ / Not specified' },
    { value: Gender.MALE, text: 'ชาย / Male' },
    { value: Gender.FEMALE, text: 'หญิง / Female' },
    { value: Gender.OTHER, text: 'อื่นๆ / Other' }
  ]
}

/**
 * Suggests gender based on selected title
 * Returns suggested gender or undefined if cannot determine
 * @param {string} titleTh - Thai title
 * @param {string} titleEn - English title
 * @returns {string|undefined} Suggested gender value
 */
export function getTitleGenderSuggestion(titleTh, titleEn) {
  // Thai title mapping
  const thaiTitleGenderMap = {
    [TitleTh.MR]: Gender.MALE,
    [TitleTh.MRS]: Gender.FEMALE,
    [TitleTh.MISS]: Gender.FEMALE,
    [TitleTh.DR_MALE]: Gender.MALE,
    [TitleTh.DR_FEMALE]: Gender.FEMALE
  }

  // English title mapping
  const englishTitleGenderMap = {
    [TitleEn.MR]: Gender.MALE,
    [TitleEn.MRS]: Gender.FEMALE,
    [TitleEn.MISS]: Gender.FEMALE
    // Dr. can be either gender, so not included
  }

  // Prioritize Thai title if available
  if (titleTh && thaiTitleGenderMap[titleTh]) {
    return thaiTitleGenderMap[titleTh]
  }

  // Fall back to English title
  if (titleEn && englishTitleGenderMap[titleEn]) {
    return englishTitleGenderMap[titleEn]
  }

  return undefined
}

/**
 * Gets full name with title prefix
 * @param {Object} user - User object
 * @param {string} language - 'th' or 'en'
 * @returns {string} Full name with title
 */
export function getFullNameWithTitle(user, language = 'th') {
  if (!user) return ''

  if (language === 'th') {
    const title = user.titleTh ? `${user.titleTh} ` : ''
    const firstName = user.firstNameTh || ''
    const lastName = user.lastNameTh || ''
    return `${title}${firstName} ${lastName}`.trim()
  } else {
    const title = user.titleEn ? `${user.titleEn} ` : ''
    const firstName = user.firstNameEn || user.firstNameTh || ''
    const lastName = user.lastNameEn || user.lastNameTh || ''
    return `${title}${firstName} ${lastName}`.trim()
  }
}

/**
 * Gets display name without title
 * @param {Object} user - User object
 * @param {string} language - 'th' or 'en'
 * @returns {string} Full name without title
 */
export function getFullNameWithoutTitle(user, language = 'th') {
  if (!user) return ''

  if (language === 'th') {
    const firstName = user.firstNameTh || ''
    const lastName = user.lastNameTh || ''
    return `${firstName} ${lastName}`.trim()
  } else {
    const firstName = user.firstNameEn || user.firstNameTh || ''
    const lastName = user.lastNameEn || user.lastNameTh || ''
    return `${firstName} ${lastName}`.trim()
  }
}

/**
 * Gets gender display text in Thai/English
 * @param {string} gender - Gender value
 * @returns {string} Display text for gender
 */
export function getGenderDisplayText(gender) {
  const genderTextMap = {
    [Gender.MALE]: 'ชาย / Male',
    [Gender.FEMALE]: 'หญิง / Female',
    [Gender.OTHER]: 'อื่นๆ / Other'
  }

  return genderTextMap[gender] || 'ไม่ระบุ / Not specified'
}
